import 'react';
import { formatCurrency, formatNumber } from '@atc/string-fns';
import _get from 'lodash/get';
import { fallbackInterestRates } from 'axl-config';
import { Text } from 'reaxl';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
const TENANTS = {
  COX_AUTO: 'CoxAuto',
  BOA: 'BOA',
  ACCELERATE: 'Accelerate',
  DEFAULT: 'default'
};
const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date ? date.toLocaleDateString('en-US') : '';
};
const buildDefaultDisclaimer = params => {
  const {
    leasePayment,
    selectedLeaseTerm,
    selectedMileage,
    acquisitionFee,
    dueAtSigningValue,
    creditScoreLow,
    make,
    programEndDate,
    grossCapCost,
    totalOfPayments,
    capReduction,
    securityDeposit,
    excessMileageCharge,
    dealerName
  } = params;
  const onPage = /*#__PURE__*/jsxs(Fragment, {
    children: ["Lease payments starting at", /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: ` ${formatCurrency(Math.floor(leasePayment))}/mo.* `
    }), "for", /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: ` ${formatNumber({
        value: selectedLeaseTerm
      })} `
    }), "months,", /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: ` ${formatNumber({
        value: selectedMileage
      })} `
    }), "total mileage with", /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: ` ${formatCurrency(Math.floor(acquisitionFee))} `
    }), "acquisition fee and", /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: ` ${formatCurrency(dueAtSigningValue)} `
    }), "due at signing."]
  });
  const modal = `This offer is available only to qualified customers with a credit score of ${creditScoreLow} or above, at participating dealers by ${make?.name ?? make} through ${programEndDate}. Advertised lease rate based on ${selectedLeaseTerm} months, ${formatCurrency(Math.floor(dueAtSigningValue))} due at lease signing, ${formatCurrency(Math.floor(acquisitionFee))} acquisition fee, including the gross capitalized cost of ${formatCurrency(Math.floor(grossCapCost))}. Excludes title, taxes, registration, license fees, dealer prep and additional options. Total monthly payments equal ${formatCurrency(Math.floor(totalOfPayments))}. Cash due at signing includes ${formatCurrency(Math.floor(capReduction))} capitalized cost reduction, a required security deposit of ${formatCurrency(Math.floor(securityDeposit))}, and first month's lease payment of ${formatCurrency(Math.floor(leasePayment))}. ${formatCurrency(excessMileageCharge)} charge per mile over ${formatNumber({
    value: selectedMileage
  })} miles per year. Early termination fees may apply. Lessee is responsible for maintenance, insurance, repair, and excess wear/tear at end of lease. Lessee will not be liable for extra charges if realized vehicle value is lower than residual value at end of lease. Lessee may incur penalties and additional charges for delinquent payments. See ${dealerName} for complete details on this and other offers.`;
  return {
    onPage,
    modal
  };
};
const buildEsntialDisclaimer = params => {
  const {
    leasePayment,
    selectedLeaseTerm,
    selectedMileage,
    salePrice,
    dueAtSigningValue
  } = params;
  const onPage = /*#__PURE__*/jsxs(Fragment, {
    children: ["*Estimated monthly payment of", ' ', /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: ` ${formatCurrency(leasePayment)} for ${formatNumber({
        value: selectedLeaseTerm
      })} months `
    }), "based on a vehicle price of ", /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: ` ${formatCurrency(salePrice)} `
    }), "for qualified lessees.", /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: ` Due at signing: ${formatCurrency(dueAtSigningValue)} `
    }), ", which includes first month's payment of", /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: ` ${formatCurrency(leasePayment)} `
    }), ", document, acquisition, and DMV fees, and applicable taxes."]
  });
  const modal = /*#__PURE__*/jsxs(Fragment, {
    children: [onPage, " Lease includes", ' ', /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: ` ${formatNumber({
        value: selectedMileage
      })} miles per year. `
    }), " Additional costs may be included for excess mileage. If the vehicle's residual value at the end of the lease differs from its realized value, an additional charge may apply at the end of the lease term.", /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: " Early lease termination fees may also apply."
    })]
  });
  return {
    onPage,
    modal
  };
};
const buildAccelerateDisclaimer = params => {
  const {
    salePrice,
    selectedMileage,
    selectedLeaseTerm,
    dueAtSigningValue,
    acquisitionFee,
    grossCapCost,
    totalOfPayments,
    capReduction,
    securityDeposit,
    leasePayment,
    excessMileageCharge,
    lender,
    dealerName,
    localizedDate,
    shortCustomDisclaimer,
    dealerFeeText = ''
  } = params;
  const onPage = `* Estimated monthly payment based on advertised price of ${formatCurrency(Math.floor(salePrice))} 
                    at ${formatNumber({
    value: selectedMileage
  })} miles / year for ${formatNumber({
    value: selectedLeaseTerm
  })} months 
                    with ${formatCurrency(Math.floor(dueAtSigningValue))} due at signing, ${formatCurrency(Math.floor(acquisitionFee))} acquisition fee, 
                    including the gross capitalized cost of ${formatCurrency(Math.floor(grossCapCost))}.${dealerFeeText}`;
  const modal = `${onPage} Excludes tax, title, registration, and license fees.Total monthly payments equal
                    ${formatCurrency(Math.floor(totalOfPayments))}. Due at signing includes
                    ${formatCurrency(Math.floor(capReduction))} capitalized cost reduction, a
                    required security deposit of ${formatCurrency(Math.floor(securityDeposit))},
                    and first month’s lease payment of ${formatCurrency(Math.floor(leasePayment))}.
                    ${formatCurrency(Math.floor(excessMileageCharge))} charge per mile over
                    ${formatNumber({
    value: selectedMileage
  })} miles per year.For
                    qualified buyers.Available to approved lessees by ${lender} at
                    participating dealers.Early termination fees may apply.Lessee is responsible for maintenance, insurance, repair, and excess
                    wear / tear at end of lease.Lessee will not be liable for extra charges if realized vehicle value is lower
                    than residual value at end of lease.Lessee may incur penalties and additional charges for delinquent
                    payments.See ${dealerName} for complete details on this and other offers.
                    Available through ${localizedDate}. This is a closed lease. ** Federal Tax Credit
                    subject to approval and not all customers will qualify.If customer chooses to receive the credit at the
                    time of purchase, the credit will be applied towards the down payment.
                    ${shortCustomDisclaimer ?? ' ' + shortCustomDisclaimer} `;
  return {
    onPage,
    modal
  };
};

/**
 * Builds lease disclaimer content based on tenant type and provided parameters
 * @param {Object} leaseData - Lease calculation data
 * @param {number} leaseData.acquisitionFee - Acquisition fee amount
 * @param {number} leaseData.capReduction - Capitalized cost reduction
 * @param {string} [leaseData.dealerName=''] - Name of the dealer
 * @param {number} leaseData.dueAtSigningValue - Amount due at signing
 * @param {number} leaseData.excessMileageCharge - Charge per excess mile
 * @param {number} leaseData.grossCapCost - Gross capitalized cost
 * @param {number} leaseData.leasePayment - Monthly lease payment amount
 * @param {Object|string} [leaseData.make=''] - Vehicle make object or string
 * @param {string} [leaseData.programEndDate=''] - Program end date
 * @param {number} leaseData.totalOfPayments - Total of all payments
 * @param {number} leaseData.securityDeposit - Security deposit amount
 * @param {string} leaseData.selectedCreditTier - Credit tier identifier
 * @param {number} leaseData.selectedLeaseTerm - Lease term in months
 * @param {number} leaseData.selectedMileage - Annual mileage allowance
 * @param {number} leaseData.salePrice - Vehicle sale price
 * @param {string} [leaseData.lender=''] - Lender name
 * @param {string} [leaseData.shortCustomDisclaimer=''] - Additional custom disclaimer text
 * @param {string} disclaimerKey - Tenant identifier (CoxAuto, BOA, etc.)
 * @param shortCustomDisclaimer
 * @param dealerFeeText
 * @returns {Object} Complete disclaimer configuration
 * @returns {boolean} returns.leaseDisclaimerHasModal - Whether disclaimer has a modal
 * @returns {JSX.Element|string} returns.onPageLeaseDisclaimer - On-page disclaimer content
 * @returns {JSX.Element|string} returns.modalText - Modal disclaimer content
 * @returns {string} returns.modalTitle - Modal title text
 * @returns {string} returns.modalTriggerText - Modal trigger button text
 */
const buildLeaseDisclaimer = (_ref, disclaimerKey, dealerFeeText) => {
  let {
    acquisitionFee,
    capReduction,
    dealerName = '',
    dueAtSigningValue,
    excessMileageCharge,
    grossCapCost,
    leasePayment,
    make = '',
    programEndDate = '',
    totalOfPayments,
    securityDeposit,
    selectedCreditTier,
    selectedLeaseTerm,
    selectedMileage,
    salePrice,
    lender = '',
    shortCustomDisclaimer = ''
  } = _ref;
  const defaultDisclaimerConfig = {
    modalTitle: 'Disclaimer Details',
    modalTriggerText: 'See more details'
  };
  const disclaimerConfigs = {
    [TENANTS.COX_AUTO]: {
      modalTitle: 'Disclaimer',
      modalTriggerText: 'Read More'
    },
    [TENANTS.BOA]: defaultDisclaimerConfig,
    [TENANTS.ACCELERATE]: defaultDisclaimerConfig,
    [TENANTS.DEFAULT]: defaultDisclaimerConfig
  };
  const creditScoreLow = _get(fallbackInterestRates, `${selectedCreditTier}.creditScoreLow`, '');
  const localizedDate = formatDate(programEndDate);
  const disclaimerConfig = disclaimerConfigs[disclaimerKey] || disclaimerConfigs[TENANTS.DEFAULT];
  const commonProps = {
    acquisitionFee,
    capReduction,
    dealerName,
    dueAtSigningValue,
    excessMileageCharge,
    grossCapCost,
    leasePayment,
    make,
    programEndDate,
    totalOfPayments,
    securityDeposit,
    selectedLeaseTerm,
    selectedMileage,
    salePrice,
    lender,
    shortCustomDisclaimer,
    creditScoreLow,
    localizedDate,
    dealerFeeText
  };
  let disclaimer;
  switch (disclaimerKey) {
    case TENANTS.COX_AUTO:
      // Esntial
      disclaimer = buildEsntialDisclaimer(commonProps);
      break;
    case TENANTS.BOA:
    case TENANTS.ACCELERATE:
      disclaimer = buildAccelerateDisclaimer(commonProps);
      break;
    default:
      disclaimer = buildDefaultDisclaimer(commonProps);
      break;
  }
  return {
    leaseDisclaimerHasModal: true,
    onPageLeaseDisclaimer: disclaimer.onPage,
    modalText: disclaimer.modal,
    modalTitle: disclaimerConfig.modalTitle,
    modalTriggerText: disclaimerConfig.modalTriggerText
  };
};
export { buildLeaseDisclaimer as default };
